import React, { Suspense } from "react";
import { Routes, Route, Navigate, useRoutes } from "react-router-dom";
import { useSelector } from "react-redux";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { TooltipProvider } from "./components/ui/tooltip";
import { Toaster } from "./components/ui/toaster";
import { useTheme } from "./hooks/useTheme";
import { WebSocketProvider } from "./contexts/WebSocketContext";
// V3 Components (New Navy/Silver Theme)
import { USE_V3_INTERFACE } from "./config/features";
import { Loader2 } from "lucide-react";
import { routesV3 } from "./routesV3";
// Legacy Components (V2)
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AppLayout from "./components/layout/AppLayout";
import Dashboard from "./pages/Dashboard";
import Processes from "./pages/Processes";
import NewProcess from "./pages/NewProcess";
import ProcessDetails from "./pages/ProcessDetails";
import ProcessEdit from "./pages/ProcessEdit";
import Documents from "./pages/Documents";
import CreateDocument from "./pages/CreateDocument";
import Editor from "./pages/Editor";
import DocumentsChat from "./pages/DocumentsChat";
import Calendar from "./pages/Calendar";
import Compliance from "./pages/Compliance";
import Library from "./pages/Library";
import Templates from "./pages/Templates";
import Metrics from "./pages/Metrics";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";
import Help from "./pages/Help";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import { ChatProvider } from "./contexts/ChatContext";
import theme, { darkTheme } from "./theme";
import Chat8 from "./pages/Chat8";

// Loading screen for V3
const LoadingScreenV3 = () => (
  <div className="min-h-screen w-full bg-navy-800 flex items-center justify-center">
    <div className="glass-card p-8 rounded-lg flex flex-col items-center gap-4">
      <Loader2 className="w-8 h-8 text-steel-400 animate-spin" />
      <p className="text-platinum-400 text-sm">Carregando LICITARS 3.0...</p>
    </div>
  </div>
);

// V3 App Component (Navy/Silver Theme)
function AppV3() {
  const { theme: currentTheme } = useTheme();
  const muiTheme = currentTheme === "dark" ? darkTheme : theme;
  const sessionId = useSelector((state: any) => state.chat?.selectedDocumentId);
  const routing = useRoutes(routesV3);
  
  return (
    <Suspense fallback={<LoadingScreenV3 />}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        <div className={currentTheme}>
          <TooltipProvider>
            <WebSocketProvider sessionId={sessionId}>
              {routing}
              <Toaster />
            </WebSocketProvider>
          </TooltipProvider>
        </div>
      </MuiThemeProvider>
    </Suspense>
  );
}

// Legacy App Component (V2)
function AppLegacy() {
  const { theme: currentTheme } = useTheme();
  const muiTheme = currentTheme === "dark" ? darkTheme : theme;
  
  // Obter sessionId do Redux para o WebSocket
  const sessionId = useSelector((state: any) => state.chat?.selectedDocumentId);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className={currentTheme}>
        <TooltipProvider>
          <WebSocketProvider sessionId={sessionId}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="processes">
                  <Route index element={<Processes />} />
                  <Route path="new" element={<NewProcess />} />
                  <Route path=":id" element={<ProcessDetails />} />
                  <Route path=":id/edit" element={<ProcessEdit />} />
                </Route>
                <Route path="documents">
                  <Route index element={<Documents />} />
                  <Route path="new" element={<CreateDocument />} />
                  <Route path="create" element={<CreateDocument />} />
                  <Route path=":id" element={<Editor />} />
                  <Route path=":id/chat" element={<DocumentsChat />} />
                </Route>
                <Route path="calendar" element={<Calendar />} />
                <Route path="compliance" element={<Compliance />} />
                <Route path="library" element={<Library />} />
                <Route path="templates" element={<Templates />} />
                <Route path="metrics" element={<Metrics />} />
                <Route path="reports" element={<Reports />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="help" element={<Help />} />
                <Route path="chat8" element={<Chat8 />} />
                <Route path="chat8/:documentId" element={<Chat8 />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            <Toaster />
          </WebSocketProvider>
        </TooltipProvider>
      </div>
    </MuiThemeProvider>
  );
}

/**
 * Main App Component
 * Conditionally renders V3 (Navy/Silver) or Legacy (V2) interface
 * Controlled by VITE_USE_V3 environment variable
 */
function App() {
  // Use V3 interface if enabled, otherwise use legacy
  if (USE_V3_INTERFACE) {
    console.log("✨ Using LICITARS 3.0 (Navy/Silver Theme)");
    return <AppV3 />;
  }
  
  console.log("📱 Using Legacy Interface (V2)");
  return <AppLegacy />;
}

export default App;
